import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CustomStatus, CustomTooltipWithLabel, downloadFile, FilterLayout, FilterProp, formatRelativeTimeForTable, renderHeaderActionButtons, SidePopover, TableDashboard, useAppDispatch, useAppSelector } from '../../../globalUtils/globalExports';
import { useNavigate } from 'react-router-dom';
import { chargerTypes, exportToCsvForAdminPasswordUpdateLogsList, fetchAdminPasswordUpdateLogsData, getChargerReducer, getCurrentFiltersValues, getCurrentScreenAppliedFiltersCount, getFilterReducer, getFilterSelectedValue, getReferenceDataReducer, setAdminPasswordUpdateLogsTableProps, setFilterData, setRedirectedStateForAdminPasswordUpdateLogsFilters } from '../../../rmsReduxStore/reduxExports';
import { GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import { cloneDeep } from 'lodash';
import { NoDataAdminPasswordUpdateLogs } from '../../../globalUtils/TableDashboard/TableComponents';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Id, toast } from 'react-toastify';
import access from '../../../auth/service/AccessControl';

function ManageAdminPasswordUpdateLogsDashboard() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { adminPasswordUpdateLogs, adminPasswordUpdateLogsCount, adminPasswordUpdateLogsTableProps, adminPasswordUpdateLogsTableListLoader, adminPasswordUpdateLogsFiltersAppliedFromRedirection } = useAppSelector(getChargerReducer);
    const { chargerVisibleIdReferenceData } = useAppSelector(getReferenceDataReducer);
    const { screenFilters } = useAppSelector(getFilterReducer);
    const [filtersVisible, setFiltersVisible] = useState(false);
    const adminPasswordUpdateLogsTablePropsRef = useRef(adminPasswordUpdateLogsTableProps);

    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [exportingFile, setExportingFile] = useState(false);

    useEffect(() => {
        adminPasswordUpdateLogsTablePropsRef.current = adminPasswordUpdateLogsTableProps
    }, [adminPasswordUpdateLogsTableProps]);

    const statusReferenceData = ['Updated','Failed','Pending'].map((item: string) => {
        return {id: item, name: item, value: item, label: item};
    });

    const handleNullValuesForFields = (value : string)=> {
        if(!value)return '-';
        else return value;
    }

    const handleChargerClick = (rowData)=>{
        navigate('/charger?chargerId=' + rowData?.chargerVisibleId , {
            state:{
                 chargerId: rowData?.chargerId,
            }
        })
    }



   const adminPasswordUpdateLogsColumn = [
        {
            field: 'chargerVisibleId',
            headerName: 'Charger',
            flex: 1,
            sortable: true,
            minWidth: 150,
            renderCell: (params) => (
                <span
                    style={{
                        color: 'var(--secondary-blue-color)',
                        fontWeight: 500,
                        cursor:'pointer'
                    }}
                    onClick={()=>handleChargerClick(params.row)}
                >
                    {handleNullValuesForFields(params.value)}
                </span>
            )
        },
        {
            field: 'customer',
            headerName: 'Customer',
            flex: 1,
            sortable: true,
            pinnable: false,
            minWidth: 120,
            renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
                const rowData = params?.row
                return (
                    <CustomTooltipWithLabel label={rowData?.customer} labelId={`admin-password-update-logs-customer-${rowData?.id}`} />
                )
            }
        },
        {
            field: 'previousValue',
            headerName: 'Old Admin Password',
            flex: 1,
            sortable: true,
            pinnable: false,
            minWidth: 120,
            renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
                const rowData = params?.row
                return (
                    <CustomTooltipWithLabel label={rowData?.previousValue} labelId={`admin-password-update-logs-old-value-${rowData?.id}`} />
                )
            }
        },
        {
            field: 'newValue',
            headerName: 'New Admin Password',
            flex: 1,
            sortable: true,
            pinnable: false,
            minWidth: 120,
            renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
                const rowData = params?.row
                return (
                    <CustomTooltipWithLabel label={rowData?.newValue} labelId={`admin-password-update-logs-customer-${rowData?.id}`} />
                )
            }
        },
        {
            field: 'updateStatus',
            headerName: 'Status',
            flex: 1,
            minWidth: 100,
            sortable: true,
            renderCell: (params) => {
                let icon = '';
                let label = '';
                switch(params.value) {
                    case 'Updated':
                        icon = 'status__device_parameter__success__icon'
                        label = 'Updated' 
                    break;
                    case 'Failed':
                        icon = 'status__device_parameter_failed__icon'
                        label = 'Failed' 
                        break;
                    case 'Pending':
                        icon = 'status__in-progress__parameter__icon',
                        label =  'Pending'
                        break;
                }
                return <CustomStatus statusText={label} statusIconClassName={icon} />
            }
        },
        // {
        //     field: 'reason',
        //     headerName: 'Reason',
        //     flex: 1,
        //     minWidth: 180,
        //     sortable: true,
        //     renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
        //         const rowData = params?.row
        //         return (
        //             <CustomTooltipWithLabel label={rowData?.reason} labelId={`admin-password-update-logs-failure-reason-${rowData?.id}`} />
        //         )
        //     }
        // },
        {
            field: 'createdAt',
            headerName: 'Requested On',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params: GridRenderCellParams<GridValidRowModel>): ReactNode => {
              const rowData = params?.row;
              const date = new Date(rowData?.createdAt);
              return formatRelativeTimeForTable(date);
            },
        },
   ];

    const layoutCallback = useCallback(
        async (
            pageNumber: number,
            pageSize: number,
            view: string,
            field: string | null,
            order: string,
            filterData,
            searchTerm: string,
        ) => {
            const tableProps = cloneDeep(adminPasswordUpdateLogsTablePropsRef?.current);
            const adminPasswordUpdateLogsFilter = getCurrentFiltersValues('adminPasswordUpdateLogs');
            const appliedFilters = filterData ? filterData : adminPasswordUpdateLogsFilter;

            await dispatch(
                fetchAdminPasswordUpdateLogsData({
                    sortBy: field || tableProps?.sortBy,
                    order: order || tableProps?.order,
                    pageSize: pageSize,
                    pageNumber: pageNumber,
                    filters: appliedFilters,
                    searchTerm: searchTerm != null ? searchTerm : tableProps?.searchTerm,
                })
            );

            if (tableProps) {
                tableProps.sortBy = field || adminPasswordUpdateLogsTablePropsRef?.current?.sortBy;
                tableProps.order = order || adminPasswordUpdateLogsTablePropsRef?.current?.order;
                tableProps.pageNumber = pageNumber;
                tableProps.pageSize = pageSize;
                if (searchTerm != null && searchTerm != undefined) {
                    tableProps.searchTerm = searchTerm;
                }
            }
            await dispatch(setAdminPasswordUpdateLogsTableProps(tableProps));
        },
        [adminPasswordUpdateLogsTablePropsRef?.current, dispatch]
    );

    const onChangeOfSearchText = useCallback(async (searchText: string) => {
            const trimmedSearchText = searchText.trim();
            const tableProps = cloneDeep(adminPasswordUpdateLogsTablePropsRef?.current);
            if (tableProps) {
                tableProps.searchTerm = trimmedSearchText;
            }
            const adminPasswordUpdateLogsFilter = getCurrentFiltersValues('adminPasswordUpdateLogs');
            await layoutCallback(
                1,
                tableProps?.pageSize,
                tableProps?.view,
                tableProps?.sortBy,
                tableProps?.order,
                adminPasswordUpdateLogsFilter,
                trimmedSearchText,
            )
        }, [adminPasswordUpdateLogsTablePropsRef?.current, layoutCallback]);
    
    const searchBoxConfiguration = useMemo(() => {
        return {
            searchBoxClassName: 'manage-admin-password-update-log-search-box',
            searchFieldId: 'manage-admin-password-update-log-search-box',
            searchFieldName: 'manage-admin-password-update-log-search-box',
            handleSearch: onChangeOfSearchText,
        };
    }, [onChangeOfSearchText, adminPasswordUpdateLogsTablePropsRef?.current]);


    const headerActionBtns = renderHeaderActionButtons({
        actionBtns:
            [
                {
                    id: 'add_filter_btn',
                    isAccessible: true,
                    headerActionBtnClick: (): void => {
                        setFiltersVisible(true);
                    },
                    badgeCount: getCurrentScreenAppliedFiltersCount('adminPasswordUpdateLogs')
                },
                {
                    id: 'export_csv_btn',
                    isAccessible: access?.updateSetting?.export(),
                    headerActionBtnClick: (event): void => {
                        setExportAnchorEl(event?.currentTarget);
                    }
                }
            ]
    });

    const handleFiltersClosePopOver = (): void => {
        setFiltersVisible(false);
    }

    const handleExportMenuClose = (): void => {
        setExportAnchorEl(null);
    };

    const fileDownloadSuccessToast = useCallback((): Id => {
        setExportingFile(false);
        return toast.success('File downloaded.');
    }, []);

    const fileDownloadFailureToast = useCallback((): Id => {
        setExportingFile(false);
        return toast.warn('Something went wrong. Please export again.');
    }, []);

    const onExportBtnClick = useCallback(async (fileType: 'csv' | 'xlsx'): Promise<void> => {
        if (exportingFile) {
            return;
        }
        try {
            const response = await dispatch(exportToCsvForAdminPasswordUpdateLogsList({
                fileType: fileType,
                filters: getCurrentFiltersValues('adminPasswordUpdateLogs'),
                searchTerm: adminPasswordUpdateLogsTablePropsRef?.current?.searchTerm
            }));
            if (response?.status === 202 || response?.status === 200) {
                downloadFile({ url: response?.data?.url });
                fileDownloadSuccessToast();
            } else {
                fileDownloadFailureToast();
                setExportAnchorEl(null);
            }
        } finally {
            setExportingFile(false);
            setExportAnchorEl(null);
        }
    }, [exportingFile, fileDownloadSuccessToast, fileDownloadFailureToast]);

    const onResetButtonClick = useCallback(async (callList: boolean) => {
        const res = await setFilterData(null, '', 'adminPasswordUpdateLogs', 'CLEAR', true)(dispatch);
        if (res?.message === 'Action dispatched successfully') {
            // Reset the filters
            if (callList && adminPasswordUpdateLogsTablePropsRef?.current) {
                const tableProps = cloneDeep(adminPasswordUpdateLogsTablePropsRef?.current);
                await layoutCallback(
                    1,
                    tableProps?.pageSize,
                    tableProps?.view,
                    tableProps?.sortBy,
                    tableProps?.order,
                    res?.filters,
                    tableProps?.searchTerm ?? '',
                )
            }
        }
    
    }, [adminPasswordUpdateLogsTablePropsRef?.current,adminPasswordUpdateLogsFiltersAppliedFromRedirection]);

     const filterFooterBtns = useMemo(() => {
        return [
            {
                buttonText: 'Reset',
                buttonId: 'Reset',
                btnClassName: 'secondary__btn',
                handleClick: (): Promise<void> => onResetButtonClick(true),
                isDisabled: false,
                buttonVariant: 'outlined',
            },
        ];
    }, [onResetButtonClick]);


    const handleFilterDataChange = useCallback(async (data, filterKey: string, isGlobal: boolean) => {
        const tableProps = cloneDeep(adminPasswordUpdateLogsTablePropsRef?.current);
        const res = await setFilterData(data, filterKey, 'adminPasswordUpdateLogs', 'SET', isGlobal)(dispatch);
        if (res?.message === 'Action dispatched successfully') {
            await layoutCallback?.(
                1,
                tableProps?.pageSize,
                tableProps?.view,
                tableProps?.sortBy,
                tableProps?.order,
                res?.filters,
                tableProps?.searchTerm ?? '',
            )
        }
    }, [adminPasswordUpdateLogsTablePropsRef]);


    const adminPasswordUpdateLogsFilterProp: FilterProp[] = useMemo(() => {
        const filterRequestedOnDateValue = getFilterSelectedValue('adminPasswordUpdateRequestedOn', 'adminPasswordUpdateLogs', false);
        return [
            {
                filterLabel: 'Charger',
                filterId: 'chargerId',
                filterType: 'dropdown',
                filterDropdownProps: {
                    selectValue: getFilterSelectedValue('chargerId', 'adminPasswordUpdateLogs', false),
                    selectOptions: chargerVisibleIdReferenceData ?? [],
                    handleSelectOptionChange: (val): void => {
                        handleFilterDataChange(val, 'chargerId', false)
                    },
                    selectDropDownId: 'admin-password-charger-filter-dropdown',
                    isMultiSelect: true
                }
            },
            {
                filterLabel: 'Status',
                filterId: 'adminPasswordUpdateStatus',
                filterType: 'dropdown',
                filterDropdownProps: {
                    selectValue: getFilterSelectedValue('adminPasswordUpdateStatus', 'adminPasswordUpdateLogs', false),
                    selectOptions: statusReferenceData ?? [],
                    handleSelectOptionChange: (val): void => {
                        handleFilterDataChange(val, 'adminPasswordUpdateStatus', false)
                    },
                    selectDropDownId: 'admin-password-update-status-filter-dropdown',
                    isMultiSelect: true
                }
            },
            {
                filterLabel: 'Requested On',
                filterId: 'adminPasswordUpdateRequestedOn',
                filterType: 'dateFilter',
                dateFilterProps: {
                    datePickerId: 'deployment-logs-requestedOn-date-picker',
                    datePickerClassName: '',
                    selectsRange: true,
                    startDate: filterRequestedOnDateValue?.[0]
                        ? new Date(filterRequestedOnDateValue?.[0])
                        : null,
                    endDate: filterRequestedOnDateValue?.[1]
                        ? new Date(filterRequestedOnDateValue?.[1])
                        : null,
                    onChange: (val): void => {
                        handleFilterDataChange(val, 'adminPasswordUpdateRequestedOn', false);
                    },
                }
            }
        ]
    }, [chargerVisibleIdReferenceData, handleFilterDataChange, screenFilters]);


    const getTableRows = useCallback((): IAdminPasswordUpdateLog[] => {
        return adminPasswordUpdateLogs !== null && adminPasswordUpdateLogs?.length > 0 ? adminPasswordUpdateLogs : []
    }, [adminPasswordUpdateLogs]);

     //Clearing the redux states
    const adminPasswordUpdateLogsListCleanUpState = useCallback(() => {
        const action = {
            type: chargerTypes.CLEAR_ADMIN_PASSWORD_UPDATE_LOGS_DATA,
        }
        dispatch(action)
        if (adminPasswordUpdateLogsFiltersAppliedFromRedirection) {
            onResetButtonClick(true)
        }
        setRedirectedStateForAdminPasswordUpdateLogsFilters(false)(dispatch)
    }, [adminPasswordUpdateLogsFiltersAppliedFromRedirection])

    useEffect(() => {
        window.addEventListener('beforeunload', adminPasswordUpdateLogsListCleanUpState);
        return (): void => {
            adminPasswordUpdateLogsListCleanUpState();
            window.removeEventListener('beforeunload', adminPasswordUpdateLogsListCleanUpState);
        }
    }, [])




    return (
        <>
            <TableDashboard
                layoutsEnabled={true}
                tableId="manage-admin-password-update-logs-table"
                tableClassName="manage_admin_password_update_logs_table"
                header='Password Reset Logs'
                searchBoxIncluded={true}
                searchBoxConfiguration={searchBoxConfiguration}
                gridColumns={adminPasswordUpdateLogsColumn}
                tableRows={getTableRows()}
                layoutView='list'
                headerActionBtns={headerActionBtns}
                listLayoutCallBack={layoutCallback}
                customNoDataComponent={NoDataAdminPasswordUpdateLogs}
                totalCount={adminPasswordUpdateLogsTableListLoader ? 0 : adminPasswordUpdateLogsCount ?? 0}
                showSkeleton={!adminPasswordUpdateLogs}
                totalCountText={'Reset Logs'}
                showLoader={adminPasswordUpdateLogsTableListLoader}
                pageNumber={adminPasswordUpdateLogsTableProps?.pageNumber}
                tablePageSize={adminPasswordUpdateLogsTableProps?.pageSize}
                sortField={adminPasswordUpdateLogsTableProps?.sortBy}
                sortOrder={adminPasswordUpdateLogsTableProps?.order}
                showRefreshButton={false}
                leftPinnedColumns={['chargerVisibleId']}
            />
            <Menu
                id="export-option-dropdown"
                anchorEl={exportAnchorEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                className='export__option__dropdown'
                sx={{ top: '30px' }}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportMenuClose}
            >
                <MenuItem>
                    <div className='export__btn-option' onClick={(e) => {
                        e?.stopPropagation();
                        onExportBtnClick('csv');
                    }}>
                        To CSV
                    </div>
                </MenuItem>
                <MenuItem>
                    <div className='export__btn-option' onClick={(e) => {
                        e?.stopPropagation();
                        onExportBtnClick('xlsx');
                    }}>
                        To Excel
                    </div>
                </MenuItem>
            </Menu>
            <>
                {
                    filtersVisible && <SidePopover
                        id={'manage-deployment-logs-filters'}
                        open={filtersVisible}
                        containerClassName={'deployment-logs-side-filters'}
                        handleClose={handleFiltersClosePopOver}
                        headerText={'Add Filters'}
                        // eslint-disable-next-line react/no-children-prop
                        children={<FilterLayout requiredReferenceDataCallbacks={['customer','chargerVisibleId']}
                            filters={adminPasswordUpdateLogsFilterProp} handleFilterDataChange={handleFilterDataChange}
                            excludeGlobalFilters={false}
                        />}
                        btnsList={filterFooterBtns}
                    />
                }
            </>
        </>
  )
}

export default ManageAdminPasswordUpdateLogsDashboard